import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from '../utils/toast';
import TopBar from '../components/TopBar';
import Loading from '../components/Loading';
import { phoneLookupAction } from '../redux/actions/phoneLookupActions';
import { PHONE_LOOKUP_RESET } from '../redux/constants/phoneLookupConstants';
import swal from 'sweetalert';
import { createUserAction, getOTp } from '../redux/actions/userActions';
import UserService from '../services/userService';
import TokenService from '../services/tokenService';

export default function PhoneLookupScreen() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, patients, error, success } = useSelector(state => state.phoneLookup);
  const { loading: createUserLoading, success: createUserSuccess, error: createUserError, data: createUserData } = useSelector(state => state.createUser);
  const { loading: otpLoading, success: otpSuccess, error: otpError } = useSelector(state => state.getOtp);

  // State for unlocked patient details dictionary: key is `hospitalId_systemId`
  const [unlockedDetails, setUnlockedDetails] = useState({});

  // State for OTP Modal
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null); // { hospitalId, systemId, hospitalName }
  const [otpInput, setOtpInput] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds

  useEffect(() => {
    let timer;
    if (otpModalOpen && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpModalOpen, countdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  useEffect(() => {
    // Reset phone lookup state when component mounts
    dispatch({ type: PHONE_LOOKUP_RESET });
  }, [dispatch]);

  // Handle createUser success - trigger OTP and navigate
  useEffect(() => {
    if (createUserSuccess && createUserData) {
      const patientData = createUserData.data?.patient;
      if (patientData) {
        // Trigger OTP sending
        dispatch(getOTp(patientData.system_id, patientData.mobile));
      }
    }

    if (createUserError) {
      // Reset createUser state on error
      dispatch({ type: 'CREATE_USER_RESET' });
    }
  }, [createUserSuccess, createUserData, createUserError, dispatch]);

  // Handle OTP success - navigate to success page
  useEffect(() => {
    if (otpSuccess && createUserSuccess && createUserData) {
      const patientData = createUserData.data?.patient;
      if (patientData) {
        // Reset states before navigation
        dispatch({ type: 'CREATE_USER_RESET' });
        dispatch({ type: 'GET_OTP_RESET' });

        // Navigate to success page with patient data
        navigate("/success", { state: patientData });
      }
    }

    if (otpError) {
      // Reset OTP state on error
      dispatch({ type: 'GET_OTP_RESET' });
    }
  }, [otpSuccess, otpError, createUserSuccess, createUserData, navigate, dispatch]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      toast.error('Please enter a phone number or email address');
      return;
    }

    dispatch(phoneLookupAction(searchTerm.trim()));
  };

  const handleInitiateUnlock = (patientData) => {
    const hospitalId = patientData.hospital.id;
    const systemId = patientData.patient.system_id;
    const hospitalName = patientData.hospital.name;

    swal({
      title: "Security Verification",
      text: "To view full patient details, an OTP will be sent to the patient's registered phone number and email address.",
      icon: "info",
      buttons: {
        cancel: {
          text: "Cancel",
          visible: true,
          closeModal: true,
        },
        confirm: {
          text: "Send OTP",
          closeModal: true,
        },
      },
    }).then(async (willSend) => {
      if (willSend) {
        setSelectedTarget({ hospitalId, systemId, hospitalName });
        setOtpInput('');
        setSendingOtp(true);
        try {
          const res = await UserService.requestDetailsOTP(hospitalId, systemId);
          toast.success(res.message || 'OTP sent successfully!');
          setCountdown(300);
          setOtpModalOpen(true);
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Failed to send OTP';
          toast.error(errorMsg);
        } finally {
          setSendingOtp(false);
        }
      }
    });
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpInput.trim()) {
      toast.error('Please enter the OTP code');
      return;
    }

    if (!selectedTarget) return;

    setVerifyingOtp(true);
    try {
      const res = await UserService.verifyDetailsOTP(
        selectedTarget.hospitalId,
        selectedTarget.systemId,
        otpInput.trim()
      );

      toast.success(res.message || 'Details unlocked successfully!');
      const unlockedKey = `${selectedTarget.hospitalId}_${selectedTarget.systemId}`;
      setUnlockedDetails(prev => ({
        ...prev,
        [unlockedKey]: res.data
      }));
      setOtpModalOpen(false);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Invalid OTP code';
      toast.error(errorMsg);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!selectedTarget) return;
    setSendingOtp(true);
    try {
      const res = await UserService.requestDetailsOTP(selectedTarget.hospitalId, selectedTarget.systemId);
      toast.success(res.message || 'OTP resent successfully!');
      setCountdown(300);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to resend OTP';
      toast.error(errorMsg);
    } finally {
      setSendingOtp(false);
    }
  };

  const handlePatientSelect = (patientData) => {
    const { hospital, patient, has_account, has_qr_code } = patientData;
    const key = `${hospital.id}_${patient.system_id}`;
    const unlockedInfo = unlockedDetails[key];

    const targetPatient = unlockedInfo ? { ...patient, ...unlockedInfo } : patient;

    // Purge previous auth session tokens and user state to prevent cross-hospital token residue
    TokenService.clearTokens();
    localStorage.removeItem('userInfo');

    // Set hospital to localStorage
    localStorage.setItem('hospital', JSON.stringify(hospital.id));
    localStorage.setItem('hospital_name', JSON.stringify(hospital.name));
    if (hospital.primary_color) {
      localStorage.setItem('hospital_primary_color', JSON.stringify(hospital.primary_color));
    }
    if (hospital.secondary_color) {
      localStorage.setItem('hospital_secondary_color', JSON.stringify(hospital.secondary_color));
    }
    if (hospital.opaque_color) {
      localStorage.setItem('hospital_opaque_color', JSON.stringify(hospital.opaque_color));
    }

    if (has_account) {
      const fileNum = targetPatient.system_id || targetPatient.file_number;
      navigate('/login', {
        state: {
          file_number: fileNum,
          system_id: fileNum,
          hospital,
        }
      });
    } else if (!has_qr_code) {
      navigate('/virtual-qr-payment', {
        state: {
          hospital,
          patient: targetPatient,
          virtualQrPrice: hospital.virtual_qr_price
        }
      });
    } else {
      swal({
        title: "Please register first!",
        text: "Do you want to register this patient?",
        icon: "warning",
        buttons: {
          cancel: {
            text: "No",
            visible: true,
            closeModal: true,
          },
          confirm: {
            text: "Yes",
            closeModal: true,
          },
        },
        dangerMode: false,
      }).then((willRegister) => {
        if (willRegister) {
          dispatch(createUserAction(targetPatient?.system_id));
        }
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (patientData) => {
    const { has_account, has_qr_code, otp_verified } = patientData;

    if (has_account && otp_verified) {
      return (
        <div className="flex items-center space-x-1 px-2 py-1 text-xs font-medium bg-gradient-to-r from-green-100 to-green-200 text-green-800 rounded-md border border-green-300">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Active Account</span>
        </div>
      );
    } else if (has_qr_code) {
      return (
        <div className="flex items-center space-x-1 px-2 py-1 text-xs font-medium bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded-md border border-blue-300">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <span>Has QR Code</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-1 px-2 py-1 text-xs font-medium bg-gradient-to-r from-orange-100 to-yellow-200 text-orange-800 rounded-md border border-orange-300">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          <span>Needs QR Code</span>
        </div>
      );
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative'>
      <TopBar title={'Patient Lookup'} url={-1} />

      {/* Full Screen Loading */}
      {(createUserLoading || otpLoading || sendingOtp) && (
        <Loading
          fullScreen={true}
          variant="medical"
          size="large"
          message={
            sendingOtp
              ? "Sending OTP to patient's registered contacts..."
              : createUserLoading
                ? "Verifying patient information..."
                : "Sending OTP to your phone..."
          }
        />
      )}

      <div className='flex justify-center items-start px-4 py-6 sm:py-8'>
        <div className='w-full max-w-2xl'>
          {/* Search Form */}
          <div className='bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6'>
            <div className='text-center mb-6'>
              <div className='w-16 h-16 bg-primary100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-800 mb-2'>
                Find Your Account
              </h1>
              <p className='text-gray-600 text-sm sm:text-base leading-relaxed'>
                Search for your patient record using your phone number or email address
              </p>
            </div>

            <form onSubmit={handleSearch} className='space-y-4'>
              <div>
                <label htmlFor="searchTerm" className='block text-sm font-medium text-gray-700 mb-2'>
                  Phone Number or Email Address
                </label>
                <input
                  type="text"
                  id="searchTerm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter your phone number or email"
                  className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary100 focus:border-transparent transition-all duration-200'
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !searchTerm.trim()}
                className='w-full py-3 px-6 bg-primary100 text-white font-semibold rounded-xl hover:bg-primary100/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95'
              >
                {loading ? (
                  <div className='flex items-center justify-center space-x-2'>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                    <span>Searching...</span>
                  </div>
                ) : (
                  'Search Patients'
                )}
              </button>
            </form>
          </div>

          {/* Search Results */}
          {loading && (
            <div className='bg-white rounded-2xl shadow-xl p-8 text-center'>
              <Loading />
              <p className='text-gray-600 mt-4'>Searching for patients...</p>
            </div>
          )}

          {(success && patients && patients.data && patients.data.length > 0) && (
            <div className='bg-white rounded-2xl shadow-xl p-6 sm:p-8'>
              <h2 className='text-xl font-bold text-gray-800 mb-6'>
                <span className='flex items-center'>
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Search Results ({patients.data.length} found)
                </span>
              </h2>

              <div className='space-y-4'>
                {patients.data.map((patientData, index) => (
                  <div
                    key={index}
                    className='group relative border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 bg-white hover:bg-gray-50 hover:border-primary100'
                  >
                    {/* Status Badge at Top */}
                    <div className='flex justify-end mb-3'>
                      {getStatusBadge(patientData)}
                    </div>

                    {/* Header with system ID and avatar */}
                    <div className='flex items-center space-x-3 mb-3'>
                      <div className='w-12 h-12 bg-primary100 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm'>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.333 0 4 .667 4 2v1H5v-1c0-1.333 2.667-2 4-2z" />
                        </svg>
                      </div>
                      <div className='flex-1 min-w-0'>
                        <h3 className='text-lg font-bold text-gray-900 group-hover:text-primary100 transition-colors'>
                          File No: {patientData.patient.system_id}
                        </h3>
                        <div className='flex items-center text-gray-600'>
                          <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H7m5 0v-5a2 2 0 00-2-2H8a2 2 0 00-2 2v5m5 0V9a2 2 0 012-2h2a2 2 0 012 2v12" />
                          </svg>
                          <span className='text-sm font-medium'>{patientData.hospital.name}</span>
                        </div>
                      </div>
                    </div>

                    {/* Patient Details - Redacted vs Unlocked */}
                    {(() => {
                      const key = `${patientData.hospital.id}_${patientData.patient.system_id}`;
                      const unlockedInfo = unlockedDetails[key];

                      if (unlockedInfo) {
                        return (
                          <div className='space-y-2 mb-4 p-3 bg-green-50/60 border border-green-200 rounded-xl transition-all animate-fadeIn'>
                            <div className='flex items-center justify-between pb-2 border-b border-green-200/60 mb-2'>
                              <span className='text-xs font-semibold text-green-700 flex items-center gap-1'>
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                </svg>
                                Full Information Unlocked
                              </span>
                              <span className='text-xs text-green-600 font-medium'>Verified</span>
                            </div>
                            <div className='grid grid-cols-2 gap-3'>
                              <div className='flex items-center space-x-2'>
                                <svg className="w-4 h-4 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <div>
                                  <span className='text-xs text-gray-500'>Name</span>
                                  <p className='text-sm font-semibold text-gray-900'>
                                    {unlockedInfo.firstname || unlockedInfo.lastname ? `${unlockedInfo.firstname || ''} ${unlockedInfo.lastname || ''}` : 'Not specified'}
                                  </p>
                                </div>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <svg className="w-4 h-4 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <div>
                                  <span className='text-xs text-gray-500'>Phone</span>
                                  <p className='text-sm font-medium text-gray-900'>{unlockedInfo.mobile || 'Not specified'}</p>
                                </div>
                              </div>
                            </div>

                            <div className='grid grid-cols-2 gap-3'>
                              <div className='flex items-center space-x-2'>
                                <svg className="w-4 h-4 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                </svg>
                                <div>
                                  <span className='text-xs text-gray-500'>Email</span>
                                  <p className='text-sm font-medium text-gray-900'>{unlockedInfo.email || 'Not specified'}</p>
                                </div>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <svg className="w-4 h-4 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <div>
                                  <span className='text-xs text-gray-500'>Gender</span>
                                  <p className='text-sm font-medium text-gray-900'>{unlockedInfo.gender || 'Not specified'}</p>
                                </div>
                              </div>
                            </div>

                            <div className='grid grid-cols-2 gap-3'>
                              <div className='flex items-center space-x-2'>
                                <svg className="w-4 h-4 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 13a2 2 0 002 2h8a2 2 0 002-2L16 7" />
                                </svg>
                                <div>
                                  <span className='text-xs text-gray-500'>Date of Birth</span>
                                  <p className='text-sm font-medium text-gray-900'>{formatDate(unlockedInfo.dob)}</p>
                                </div>
                              </div>
                            </div>

                            {unlockedInfo.address && (
                              <div className='flex items-center space-x-2'>
                                <svg className="w-4 h-4 text-primary100 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div className='flex-1'>
                                  <span className='text-xs text-gray-500'>Address</span>
                                  <p className='text-sm font-medium text-gray-900'>{unlockedInfo.address}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <div className='mb-4 p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-2'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center space-x-2 text-gray-600 text-xs'>
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              <span>Patient Details</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleInitiateUnlock(patientData)}
                            className='w-full py-2 px-3 bg-white border border-primary100/40 text-primary100 font-medium text-xs rounded-lg hover:bg-primary100/5 transition-all flex items-center justify-center space-x-2 shadow-sm'
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>View Details (Requires OTP)</span>
                          </button>
                        </div>
                      );
                    })()}

                    {/* Payment Required Notice */}
                    {!patientData.has_qr_code && (
                      <div className='mb-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg'>
                        <div className='flex items-center space-x-2'>
                          <svg className="w-4 h-4 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          <div className='flex-1'>
                            <p className='text-sm font-medium text-yellow-800'>
                              Payment Required for Virtual QR Code
                            </p>
                            <p className='text-sm text-yellow-700'>
                              Amount: <span className='font-bold'>₦{patientData.hospital.virtual_qr_price}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <div>
                      <button
                        onClick={() => handlePatientSelect(patientData)}
                        className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-sm ${patientData.has_account
                            ? 'bg-primary100 hover:bg-primary100/90'
                            : patientData.has_qr_code
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:opacity-90'
                              : 'bg-gradient-to-r from-orange-600 to-yellow-600 hover:opacity-90'
                          }`}
                      >
                        <div className='flex items-center justify-center space-x-2'>
                          {patientData.has_account ? (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                              </svg>
                              <span>Login to Account</span>
                            </>
                          ) : patientData.has_qr_code ? (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                              </svg>
                              <span>Register Account</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <span>Pay ₦{patientData.hospital.virtual_qr_price}</span>
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {((success && patients && patients.data && patients.data.length === 0) ||
            (error && (error.includes('No patients found') || error.includes('not found')))) && (
              <div className='bg-white rounded-2xl shadow-xl p-8 text-center'>
                <div className='w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6'>
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className='text-xl font-bold text-gray-800 mb-3'>No Patients Found</h3>
                <p className='text-gray-600 text-base mb-4'>
                  We couldn't find any patient records matching your search term.
                </p>
                <div className='bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6'>
                  <h4 className='text-sm font-semibold text-blue-800 mb-2'>Search Tips:</h4>
                  <ul className='text-sm text-blue-700 space-y-1 text-left'>
                    <li>• Make sure you're using the phone number registered with your healthcare provider</li>
                    <li>• Try searching with your email address if you provided one</li>
                    <li>• Check for any typos in your phone number or email</li>
                    <li>• Contact your healthcare provider if you still can't find your record</li>
                  </ul>
                </div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    dispatch({ type: PHONE_LOOKUP_RESET });
                  }}
                  className='px-6 py-3 bg-primary100 text-white font-semibold rounded-xl hover:bg-primary100/90 transition-colors duration-200'
                >
                  Try Another Search
                </button>
              </div>
            )}

          {/* Help Section */}
          <div className='bg-white rounded-xl shadow-lg p-4 mt-6'>
            <h3 className='text-sm font-semibold text-gray-800 mb-3 text-center'>Need Help?</h3>
            <div className='space-y-2 text-xs text-gray-600'>
              <p>• Use the phone number registered with your healthcare provider</p>
              <p>• Email search is also supported if you provided one</p>
              <p>• Contact your healthcare provider if you can't find your record</p>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      {otpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 transform transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-primary100/10 text-primary100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <button
                type="button"
                onClick={() => setOtpModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Enter Security OTP
            </h3>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              An OTP has been sent to the patient's registered phone number and email address for File No: <span className="font-semibold text-gray-900">{selectedTarget?.systemId}</span> at {selectedTarget?.hospitalName}.
            </p>

            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between text-xs text-amber-800">
              <div className="flex items-center space-x-1.5">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Code Expires In:</span>
              </div>
              <span className="font-mono font-bold text-amber-900 text-sm">{formatTime(countdown)}</span>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label htmlFor="otpInput" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  6-Digit Verification Code
                </label>
                <input
                  type="text"
                  id="otpInput"
                  value={otpInput}
                  maxLength={6}
                  onChange={(e) => setOtpInput(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Enter 6-digit code"
                  className="w-full text-center text-2xl tracking-[0.4em] font-mono py-3 px-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary100 focus:border-transparent transition-all"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={verifyingOtp || !otpInput.trim()}
                className="w-full py-3 px-4 bg-primary100 text-white font-semibold rounded-xl hover:bg-primary100/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md flex items-center justify-center space-x-2"
              >
                {verifyingOtp ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <span>Verify & Unlock Details</span>
                )}
              </button>
            </form>

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={sendingOtp}
                className="text-primary100 font-semibold hover:underline disabled:opacity-50"
              >
                Resend OTP Code
              </button>
              <button
                type="button"
                onClick={() => setOtpModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}